import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, CheckSquare } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["open", "in_progress", "done", "blocked"];
const STATUS_COLORS = {
  open: "#94A3B8", in_progress: "#EAB308", done: "#10B981", blocked: "#EF4444",
};
const PRIORITY_COLORS = { low: "#10B981", medium: "#EAB308", high: "#F97316", critical: "#EF4444" };

export default function Tasks() {
  const [tasks, setTasks] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ priority: "medium", status: "open" });

  const load = () => api.get("/tasks").then((r) => setTasks(r.data.tasks));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.title) return toast.error("Título requerido");
    await api.post("/tasks", form);
    toast.success("Tarea creada");
    setForm({ priority: "medium", status: "open" });
    setDlg(false);
    load();
  };

  const setStatus = async (t, status) => {
    await api.patch(`/tasks/${t.id}`, { status });
    load();
  };

  const grouped = STATUSES.map((s) => ({ status: s, items: tasks.filter((t) => t.status === s) }));

  return (
    <div className="p-8 space-y-6" data-testid="tasks-page">
      <div className="border-b border-[var(--border)] pb-5 flex items-end justify-between">
        <div>
          <div className="label-mono mb-2">EJECUCIÓN OPERATIVA</div>
          <h1 className="font-display font-black text-4xl tracking-tight">Tareas</h1>
          <p className="text-sm text-[var(--text-dim)] mt-1">
            Kanban de tareas asignadas a personas o agentes.
          </p>
        </div>
        <Dialog open={dlg} onOpenChange={setDlg}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="task-add">
              <Plus className="w-4 h-4 mr-2" /> Nueva tarea
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
            <DialogHeader><DialogTitle className="font-display">Nueva tarea</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="label-mono">Título</Label>
                <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })}
                       className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="task-title" />
              </div>
              <div>
                <Label className="label-mono">Descripción</Label>
                <Input value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
                       className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="task-desc" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-mono">Owner Agent</Label>
                  <Input value={form.owner_agent || ""} onChange={(e) => setForm({ ...form, owner_agent: e.target.value })}
                         placeholder="sales, marketing, ..." className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="task-owner" />
                </div>
                <div>
                  <Label className="label-mono">Prioridad</Label>
                  <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                    <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="task-priority">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                      {["low","medium","high","critical"].map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="task-submit">
                Crear
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {grouped.map((col) => (
          <div key={col.status} className="border border-[var(--border)] bg-[var(--surface)] p-4" data-testid={`col-${col.status}`}>
            <div className="flex items-center justify-between mb-3">
              <div className="label-mono flex items-center gap-2">
                <div className="w-2 h-2" style={{ background: STATUS_COLORS[col.status] }} />
                {col.status.replace("_", " ")}
              </div>
              <span className="font-mono text-xs text-[var(--text-dim)]">{col.items.length}</span>
            </div>
            <div className="space-y-2">
              {col.items.map((t) => (
                <div key={t.id} className="border border-[var(--border)] bg-[var(--bg)] p-3" data-testid={`task-${t.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <Badge className="rounded-none font-mono text-[9px]" style={{ background: PRIORITY_COLORS[t.priority], color: "#0B0E14" }}>
                      {t.priority}
                    </Badge>
                    {t.owner_agent && (
                      <span className="font-mono text-[10px] text-[var(--accent)] uppercase">{t.owner_agent}</span>
                    )}
                  </div>
                  <div className="text-sm font-semibold text-white leading-tight mb-2">{t.title}</div>
                  {t.description && <div className="text-xs text-[var(--text-dim)] leading-relaxed line-clamp-2 mb-2">{t.description}</div>}
                  <Select value={t.status} onValueChange={(v) => setStatus(t, v)}>
                    <SelectTrigger className="rounded-none bg-[var(--surface)] border-[var(--border)] font-mono text-[10px] h-7" data-testid={`task-set-${t.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                      {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              {col.items.length === 0 && (
                <div className="text-xs text-[var(--text-dim)] font-mono py-4 text-center border border-dashed border-[var(--border)]">
                  vacío
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
